import { useCallback, useEffect, useRef, useState } from "react";
import DirectDebitForm from "../components/DirectDebitForm";
import DirectDebitGuarantee from "../components/DirectDebitGuarantee";
import DirectDebitDownload from "../components/DirectDebitDownload";
import SigningLinkStatus, { type SigningLinkVariant } from "../components/SigningLinkStatus";
import {
  getMandate,
  signMandate,
  MandateApiFailure,
  SignMandateRequest,
  MandateResponse,
  ModulusCheck,
  originatorNameFromDetails,
  signingIssueFromErrorResult,
} from "../components/services/mandate.service";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CircleCheckBig, Loader2 } from "lucide-react";
import LogoBlack from "../assets/logo-black.png";

const emptyFormData = () => ({
  originatorCompanyName: "",
  originatorCompanyAddress1: "",
  originatorCompanyAddress2: "",
  name: "",
  email: "",
  accountHolderName: "",
  bankAccountNo: "",
  sortCode: "",
  bankAddress: "",
  serviceUserNumber: "",
  reference: "",
  signature: "",
  date: new Date().toISOString().split("T")[0],
  terms: false,
});

interface PageBarrier {
  variant: SigningLinkVariant;
  originatorCompanyName?: string;
}

const HomePage = () => {
  const [token] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("token");
  });

  const [barrier, setBarrier] = useState<PageBarrier | null>(null);
  /** True while validating token / loading mandate (only when signing link may be usable) */
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(!!token);
  /** Form + flow once mandate is awaiting signature */
  const [mandateLoaded, setMandateLoaded] = useState(false);

  const [isloading, setIsloading] = useState(false);
  const [disableDownloadCard, setDisableDownloadCard] = useState(true);
  const [disableFormCard, setDisableFormCard] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [modulusCheck, setModulusCheck] = useState<ModulusCheck | undefined>(
    undefined
  );

  const [data, setData] = useState<any>(() => emptyFormData());
  const [step, setStep] = useState(0);

  const originatorHintRef = useRef<string | undefined>(undefined);

  const applySigningFailure = useCallback((failure: MandateApiFailure) => {
    const issue = signingIssueFromErrorResult(failure);
    const hint =
      originatorNameFromDetails(failure.details) ?? originatorHintRef.current;
    if (issue === "expired") {
      setBarrier({ variant: "expired", originatorCompanyName: hint });
    } else if (issue === "invalid") {
      setBarrier({ variant: "invalid", originatorCompanyName: hint });
    } else {
      setBarrier({ variant: "unavailable", originatorCompanyName: hint });
    }
  }, []);

  const handleFinalSubmission = async () => {
    if (data.terms) {
      if (!token) {
        setBarrier({ variant: "missing" });
        return;
      }

      const signedData: SignMandateRequest = {
        signature: data.signature,
        soleSignatory: data.terms,
        signatureType: "ELECTRONIC",
      };
      try {
        setIsloading(true);
        const response = await signMandate(token, signedData);
        if (!response.ok) {
          applySigningFailure(response);
          return;
        }
        if (response.data.signed === true) {
          setStep(4);
        }
      } catch (error) {
        console.error("Failed to register mandate:", error);
        applySigningFailure({ ok: false, status: 0, message: String(error) });
      } finally {
        setIsloading(false);
      }
    } else {
      setStep(4);
    }
  };

  const handleContinueButton = () => {
    setDisableDownloadCard(false);
    setDisableFormCard(true);
  };

  useEffect(() => {
    async function bootstrap() {
      if (!token) {
        setBarrier({ variant: "missing" });
        setIsBootstrapping(false);
        return;
      }

      originatorHintRef.current = undefined;
      setBarrier(null);
      setMandateLoaded(false);
      setIsBootstrapping(true);
      setData(emptyFormData());
      try {
        const mandateResponse = await getMandate(token);

        if (!mandateResponse.ok) {
          applySigningFailure(mandateResponse);
          setIsBootstrapping(false);
          return;
        }

        const mandate = mandateResponse.data as MandateResponse;
        const hint = mandate.originatorContact?.companyName;
        originatorHintRef.current = hint;

        if (
          mandate.status &&
          mandate.status !== "AWAITING_SIGNATURE"
        ) {
          setBarrier({
            variant: "alreadySigned",
            originatorCompanyName: hint,
          });
          setIsBootstrapping(false);
          return;
        }

        if (mandate.modulusCheck) {
          if (mandate.modulusCheck.checked === false) {
            console.error(
              "Modulus check failed:",
              mandate.modulusCheck.error
            );
          }
          setModulusCheck(mandate.modulusCheck);
        }

        setData((prevData: any) => ({
          ...prevData,
          name: mandate.destinationContact?.businessName ?? "",
          email: mandate.destinationContact?.email ?? "",
          accountHolderName: mandate.destination?.accountName ?? "",
          bankAccountNo: mandate.destination?.accountNumber ?? "",
          sortCode: mandate.destination?.sortCode ?? "",
          reference: mandate.auddisReference,
          date: mandate?.createdAt?.toString().split("T")[0],
          originatorCompanyName: mandate.originatorContact?.companyName ?? "",
          originatorCompanyAddress1: mandate.originatorContact?.addressLine1 ?? "",
          originatorCompanyAddress2: mandate.originatorContact?.addressLine2 ?? "",
          serviceUserNumber: mandate.serviceUserNumber,
        }));
        setMandateLoaded(true);
      } catch (error) {
        console.error("Failed to fetch mandate data:", error);
        applySigningFailure({
          ok: false,
          status: 0,
          message: String(error),
        });
      } finally {
        setIsBootstrapping(false);
      }
    }
    bootstrap();
  }, [token, applySigningFailure]);

  if (barrier) {
    return (
      <SigningLinkStatus
        variant={barrier.variant}
        originatorCompanyName={barrier.originatorCompanyName}
      />
    );
  }

  if (token && isBootstrapping && !mandateLoaded) {
    return (
      <div className="flex min-h-[40vh] w-full items-center justify-center">
        <Loader2 className="w-28 h-28 animate-spin text-yavrio-green" />
      </div>
    );
  }

  const showForm = !!token && mandateLoaded;

  return (
    <>
      {showForm && (
        <>
          <div
            className={`flex flex-row gap-4 items-start transition-all duration-500 transform ${
              step === 0 ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"
            }`}
          >
            {step === 0 && (
              <div className="flex gap-10 opacity-100">
                <div
                  className={`relative ${isloading ? "opacity-25" : "opacity-100"}`}
                >
                  <DirectDebitForm
                    data={data}
                    setData={setData}
                    handleSubmitButton={
                      data.terms ? handleFinalSubmission : handleContinueButton
                    }
                    disableFormCard={disableFormCard}
                    modulusCheck={modulusCheck}
                  />
                  {isloading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-28 h-28 animate-spin text-yavrio-green" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4">
                  <div
                    className={
                      disableDownloadCard
                        ? "transition-all duration-500 opacity-25"
                        : "transition-all duration-500 opacity-100"
                    }
                  >
                    <DirectDebitDownload
                      data={data}
                      handleSubmitButton={() => handleFinalSubmission()}
                      disabled={disableDownloadCard}
                      token={token || undefined}
                      onFileUploaded={(file) => setUploadedFile(file)}
                      onSigningTokenFailure={applySigningFailure}
                    />
                  </div>
                  <DirectDebitGuarantee data={data} />
                </div>
              </div>
            )}
          </div>
          <div
            className={`transition-all duration-500 transform ${
              step === 4 ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"
            }`}
          >
            {step === 4 && (
              <Card className="flex flex-col w-full mx-auto p-10 max-w-7xl transition-all duration-500 opacity">
                <CardHeader className="flex flex-col items-center gap-8">
                  <CircleCheckBig className="w-20 h-20 text-yavrio-green mx-auto" />
                  <CardTitle className="text-4xl font-bold text-center text-yavrio-charcoal">
                    Mandate Signed Successfully!
                  </CardTitle>
                  <div className="flex flex-col gap-16">
                    <div className="flex flex-col gap-4 text-center text-lg">
                      <p className="mb-6">
                        Thank you for using Yavrio.
                        <br />
                        {uploadedFile
                          ? "If you would like to download your mandate, click the button below."
                          : "If you would like to print your mandate, click the button below."}
                      </p>
                      {uploadedFile ? (
                        <Button
                          variant="yvrSecondary"
                          className="rounded-none w-1/3 self-center gap-2 text-gray-700"
                          onClick={() => {
                            try {
                              const url = window.URL.createObjectURL(uploadedFile);
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = uploadedFile.name;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error("Failed to download mandate:", error);
                            }
                          }}
                        >
                          Download Mandate
                        </Button>
                      ) : (
                        <DirectDebitDownload
                          data={data}
                          printOnly
                          token={token || undefined}
                        />
                      )}
                      <p className="mt-8">
                        You may now close this page.
                        <br /> Having issues? Please contact{" "}
                        <a
                          href="mailto:support@yavr.io"
                          className="text-blue-500"
                        >
                          support@yavr.io
                        </a>
                      </p>
                    </div>
                    <img
                      src={LogoBlack}
                      alt="yavrio logo"
                      className="w-1/4 mx-auto mt-12"
                      style={{ objectFit: "contain", objectPosition: "center" }}
                    />
                  </div>
                </CardHeader>
              </Card>
            )}
          </div>
        </>
      )}

      <img
        className="absolute -left-5 -bottom-5 opacity-20 w-1/4 -z-10"
        src="https://yavrio-public.s3.eu-west-2.amazonaws.com/assets/images/Yavrio_icon.svg"
        alt=""
      />
    </>
  );
};

export default HomePage;
